import React, { useState } from "react";
import { Badge, Button } from "react-bootstrap";
import moment from "moment-timezone";
import { toast } from "react-toastify";

import { useApiGet } from "../../../hooks/useApi";

const stripeSummary = (write) => {
  const stripe = write && write.stripe;
  if (!stripe) {
    return null;
  }

  const parts = [];
  if (stripe.paymentIntentId) {
    parts.push(`PI ${stripe.paymentIntentId}`);
  }
  if (stripe.refundId) {
    parts.push(`refund ${stripe.refundId}`);
  }
  if (stripe.reverse_transfer) {
    parts.push("reverse_transfer");
  }
  if (stripe.refund_application_fee) {
    parts.push("refund_application_fee");
  }
  if (stripe.payoutId) {
    parts.push(`payout ${stripe.payoutId}`);
  }
  if (stripe.status) {
    parts.push(stripe.status);
  }
  return parts.length ? parts.join(" · ") : null;
};

const JsonToggle = ({ label, value }) => {
  const [open, setOpen] = useState(false);

  if (value == null) {
    return null;
  }

  return (
    <div className="mt-1">
      <Button
        variant="link"
        size="sm"
        className="p-0"
        onClick={() => setOpen((prev) => !prev)}
      >
        {open ? "Hide" : "Show"} {label}
      </Button>
      {open ? (
        <pre
          className="mb-0 mt-1 p-2"
          style={{
            fontSize: "11px",
            background: "#f8f9fa",
            maxHeight: 220,
            overflow: "auto",
          }}
        >
          {JSON.stringify(value, null, 2)}
        </pre>
      ) : null}
    </div>
  );
};

const EventRow = ({ row, isThisOccurrence }) => {
  const [open, setOpen] = useState(false);
  const moneySummary = stripeSummary(row.write);

  return (
    <div className="mb-3 pb-3" style={{ borderBottom: "1px solid #eee" }}>
      <Button
        variant="link"
        className="p-0 text-decoration-none text-dark text-start w-100"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <p className="text-muted mb-1">
          {open ? "▾" : "▸"} {moment(row.at).format("MM/DD/YYYY h:mm A")}
        </p>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-0">
          <Badge
            bg={row.entity === "job_request" ? "primary" : "secondary"}
            text={row.entity === "job_request" ? "dark" : undefined}
          >
            {row.entity === "job_request" ? "Job" : "Occurrence"}
          </Badge>
          <strong>{row.action}</strong>
          <span className="text-muted">{row.source}</span>
          {isThisOccurrence ? <Badge bg="info">This occurrence</Badge> : null}
        </div>
      </Button>

      {open ? (
        <div className="mt-2">
          <p className="mb-1">
            {row.actorName ||
              (row.actorUserId ? String(row.actorUserId) : "System")}
            {row.handler ? (
              <span className="text-muted"> · {row.handler}</span>
            ) : null}
          </p>
          {moneySummary ? (
            <p className="mb-1" style={{ fontSize: "13px" }}>
              Stripe: {moneySummary}
            </p>
          ) : null}
          <JsonToggle label="request" value={row.request} />
          <JsonToggle label="write" value={row.write} />
        </div>
      ) : null}
    </div>
  );
};

const JobRequestEventLog = ({ jobRequestId, emphasizeEventId = null }) => {
  const [expanded, setExpanded] = useState(false);

  const { isLoading, data } = useApiGet(
    `job-request-activity-${jobRequestId}`,
    `/admin/job-requests/${jobRequestId}/activity`,
    undefined,
    {
      enabled: Boolean(jobRequestId) && expanded,
      onError: (err) => toast.error(err.message),
      staleTime: 0,
    }
  );

  if (!jobRequestId) {
    return null;
  }

  const rows = Array.isArray(data) ? data : [];

  return (
    <div>
      <Button
        variant="link"
        className="p-0 text-decoration-none text-dark"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <h5 className="mb-0 d-inline">
          {expanded ? "▾" : "▸"} Event log
          {!isLoading && expanded && rows.length > 0
            ? ` (${rows.length})`
            : ""}
        </h5>
      </Button>

      {expanded ? (
        <div className="mt-3">
          {isLoading ? (
            <p className="mb-0">Loading event log...</p>
          ) : rows.length === 0 ? (
            <p className="text-muted mb-0">
              No recorded events yet. Activity is captured going forward.
            </p>
          ) : (
            rows.map((row) => {
              const isThisOccurrence =
                emphasizeEventId &&
                row.jobRequestEventId &&
                String(row.jobRequestEventId) === String(emphasizeEventId);

              return (
                <EventRow
                  key={row._id}
                  row={row}
                  isThisOccurrence={Boolean(isThisOccurrence)}
                />
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
};

export default JobRequestEventLog;
